import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicPageLayout from '../../components/layout/PublicPageLayout';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import SchemaMarkup from '../../components/SchemaMarkup';

// IMPORTED
const { FiAlertTriangle, FiCheckCircle, FiShield, FiArrowRight } = FiIcons;

const HmsVarslingPage = () => {
    const navigate = useNavigate(); // Hook for navigasjon

  const faqData = [
    { q: "Kan man varsle om arbeidsmiljø?", a: "Ja, dårlig arbeidsmiljø, destruktiv ledelse eller manglende sikkerhetstiltak er typiske eksempler på kritikkverdige forhold." },
    { q: "Er det lov å varsle eksternt?", a: "Ja, man har alltid rett til å varsle til tilsynsmyndigheter (f.eks. Arbeidstilsynet). Man kan også varsle eksternt til media hvis man er i god tro om at det faktisk foreligger kritikkverdige forhold og man har forsøkt å varsle internt først (eller det ikke er hensiktsmessig)." },
    { q: "Hva er forskjellen på et varsel og en klage?", a: "En klage gjelder ofte forhold som kun angår en selv (f.eks. misnøye med lønn), mens et varsel gjelder forhold som er i strid med lover, regler eller etiske normer og som ofte har en allmenn interesse." }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };

  return (
    <PublicPageLayout 
      title="Hva er HMS varsling?" 
      subtitle="En komplett guide til varsling om kritikkverdige forhold på arbeidsplassen."
      activePage="/hms-varsling"
    >
      <SchemaMarkup data={faqSchema} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">

        {/* Intro */}
        <section className="prose prose-lg prose-slate max-w-none">
          <p className="text-xl leading-relaxed text-slate-600 font-medium">
            HMS varsling er en lovpålagt mekanisme som sikrer at ansatte trygt kan si ifra om kritikkverdige forhold på arbeidsplassen uten frykt for gjengjeldelse. Det er en hjørnestein i et sunt arbeidsmiljø og en viktig del av virksomhetens internkontroll.
          </p>
        </section>

        {/* Kritikkverdige Forhold List */}
        <section className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <SafeIcon icon={FiAlertTriangle} className="text-orange-500" />
            Hva regnes som kritikkverdige forhold?
          </h2>
          <p className="text-slate-600 mb-6">
            Arbeidsmiljøloven definerer kritikkverdige forhold som forhold som er i strid med rettsregler, skriftlige etiske retningslinjer i virksomheten eller etiske normer som det er bred tilslutning til i samfunnet.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Brudd på lover og forskrifter",
              "Brudd på interne HMS-rutiner",
              "Trakassering eller mobbing",
              "Diskriminering",
              "Fare for liv og helse",
              "Klima- eller miljøkriminalitet",
              "Korrupsjon eller økonomisk kriminalitet",
              "Myndighetsmisbruk"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <SafeIcon icon={FiCheckCircle} className="text-blue-600 mt-1 flex-shrink-0" />
                <span className="text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Employers Responsibility */}
        <section className="space-y-6">
          <h2 className="text-3xl font-bold text-slate-900">Arbeidsgivers ansvar og plikter</h2>
          <div className="text-lg text-slate-600 leading-relaxed space-y-4">
            <p>
              Alle virksomheter med 5 eller flere ansatte plikter å ha skriftlige rutiner for intern varsling. Rutinene skal være lett tilgjengelige for alle ansatte og beskrive fremgangsmåten for varsling.
            </p>
            <p>
              Når et varsel mottas, har arbeidsgiver en aktivitetsplikt. Dette innebærer at varselet må undersøkes innen rimelig tid. Arbeidsgiver skal sørge for at varsleren har et fullt forsvarlig arbeidsmiljø, og at den det varsles om blir ivaretatt i henhold til kontradiksjonsprinsippet.
            </p>
          </div>
        </section>

        {/* How We Solve It - FIXED BUTTON */}
        <section className="bg-blue-50 rounded-2xl p-8 md:p-12 my-12">
          <div className="max-w-2xl mx-auto text-center">
            <SafeIcon icon={FiShield} className="text-blue-600 text-4xl mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Trygghet satt i system</h2>
            <p className="text-slate-700 mb-8 leading-relaxed">
              HMSVarsling.no leverer en ferdig plattform som dekker alle lovkravene. Vi gir dere en digital postkasse for varsler, ferdige maler for saksbehandling, og muligheten for anonym dialog med varsler. Dette fjerner usikkerheten rundt prosessen og sikrer at dere håndterer saker korrekt fra dag én.
            </p>
            {/* FIKSET NAVIGASJON HER */}
            <button 
                onClick={() => navigate('/company/register')} 
                className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              Opprett varslingskanal <SafeIcon icon={FiArrowRight} />
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="border-t border-gray-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Ofte stilte spørsmål om HMS varsling</h2>
          <div className="space-y-6">
            {faqData.map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

      </article>
    </PublicPageLayout>
  );
};

export default HmsVarslingPage;